// eslint-disable-next-line max-classes-per-file
import { type AnyChainflipAsset } from '@chainflip/utils/chainflip';
import BigNumber from 'bignumber.js';
import { anyChainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue, formatWithNumeral, removeTrailingZeros } from '@/shared/utils/numbers';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type TokenCast<T, U> =
  // if the value to cast is an empty array, return an empty array
  T extends []
    ? []
    : // recursively cast array items
      T extends [infer A, ...infer B]
      ? [TokenCast<A, U>, ...TokenCast<B, U>]
      : // recursively cast object values
        T extends Record<string, any>
        ? { [K in keyof T]: K extends U ? TokenAmount : TokenCast<T[K], U> }
        : T;
/* eslint-enable @typescript-eslint/no-explicit-any */

type InputValue = TokenAmount | BigNumber.Value | bigint;

const ROUNDING_MODE = {
  ROUND_UP: 0,
  ROUND_DOWN: 1,
  ROUND_CEIL: 2,
  ROUND_FLOOR: 3,
  ROUND_HALF_UP: 4,
  ROUND_HALF_DOWN: 5,
  ROUND_HALF_EVEN: 6,
  ROUND_HALF_CEIL: 7,
  ROUND_HALF_FLOOR: 8,
} as const;

type RoundingMode = keyof typeof ROUNDING_MODE;

const DEFAULT_DECIMAL_PLACES: number = 18;

const STRINGIFIED_TOKEN_AMOUNT_FLAG = '_isStringifiedTokenAmount';
export default class TokenAmount {
  protected static readonly defaultDecimalPlaces = DEFAULT_DECIMAL_PLACES;

  static readonly ZERO = new this(0, this.defaultDecimalPlaces);

  static castObject<T, const K extends readonly string[]>(
    obj: T,
    keys: K,
  ): TokenCast<T, K[number]> {
    if (Array.isArray(obj)) {
      return obj.map((item) => TokenAmount.castObject(item, keys)) as TokenCast<T, K[number]>;
    }

    if (typeof obj === 'object' && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => {
          if (keys.includes(key) && typeof value === 'string') {
            return [key, new this(value)];
          }
          return [key, TokenAmount.castObject(value, keys)];
        }),
      ) as TokenCast<T, K[number]>;
    }

    return obj as TokenCast<T, K[number]>;
  }

  static from(value: BigNumber.Value | null | undefined): TokenAmount | null {
    if (value === null || value === undefined) return null;

    try {
      return new this(value);
    } catch {
      return null;
    }
  }

  static fromAsset(value: BigNumber.Value | bigint | number, asset: AnyChainflipAsset): TokenAmount;
  static fromAsset(value: null | undefined, asset: AnyChainflipAsset): null;
  static fromAsset(
    value: BigNumber.Value | bigint | null | undefined,
    asset: AnyChainflipAsset,
  ): TokenAmount | null;
  static fromAsset(
    value: BigNumber.Value | bigint | null | undefined,
    asset: AnyChainflipAsset,
  ): TokenAmount | null {
    if (value === null || value === undefined) return null;

    try {
      const { decimals, isStablecoin } = anyChainflipAssetMap[asset];
      return new this(value, decimals, isStablecoin);
    } catch {
      return null;
    }
  }

  static fromWholeUnits(
    value: BigNumber.Value,
    decimalPlacesOrAsset: number | AnyChainflipAsset = this.defaultDecimalPlaces,
  ): TokenAmount {
    const decimalPlaces =
      typeof decimalPlacesOrAsset === 'number'
        ? decimalPlacesOrAsset
        : anyChainflipAssetMap[decimalPlacesOrAsset].decimals;
    const isStablecoin =
      typeof decimalPlacesOrAsset === 'number'
        ? undefined
        : anyChainflipAssetMap[decimalPlacesOrAsset].isStablecoin;

    return new TokenAmount(
      new BigNumber(value).shiftedBy(decimalPlaces),
      decimalPlaces,
      isStablecoin,
    );
  }

  static sum(amounts: BigNumber.Value[], decimalPlaces?: number): TokenAmount {
    return new this(BigNumber.sum(0, ...amounts), decimalPlaces ?? this.defaultDecimalPlaces);
  }

  static clone({ decimalPlaces }: { decimalPlaces: number }): typeof TokenAmount {
    return class extends TokenAmount {
      protected static readonly defaultDecimalPlaces = decimalPlaces;

      static readonly ZERO = new this(0);

      constructor(value: InputValue) {
        super(value, decimalPlaces);
      }
    };
  }

  static areEqual(a: TokenAmount | undefined, b: TokenAmount | undefined): boolean {
    if (a === undefined || b === undefined) return a === b;
    return a.eq(b);
  }

  private bigNumber: BigNumber | undefined;

  readonly value: BigNumber;

  readonly decimalPlaces;

  readonly isStablecoin: boolean;

  constructor(value: InputValue, decimalPlaces?: number, isStablecoin?: boolean) {
    this.decimalPlaces = decimalPlaces ?? this.Class.defaultDecimalPlaces;
    this.isStablecoin = isStablecoin ?? (value instanceof TokenAmount && value.isStablecoin);
    if (value instanceof TokenAmount) {
      this.value = value.value;
    } else {
      const coerced = typeof value === 'bigint' ? value.toString() : value;
      this.value = new (BigNumber.clone({
        DECIMAL_PLACES: this.decimalPlaces,
      }))(coerced);
    }
    this.value = this.value.decimalPlaces(0, BigNumber.ROUND_DOWN);
  }

  private get Class(): typeof TokenAmount {
    return this.constructor as typeof TokenAmount;
  }

  add(amount: TokenAmount | BigNumber.Value): TokenAmount {
    if (amount instanceof TokenAmount) {
      return new this.Class(this.value.plus(amount.value), this.decimalPlaces, this.isStablecoin);
    }

    return new this.Class(this.value.plus(amount), this.decimalPlaces, this.isStablecoin);
  }

  sub(amount: TokenAmount | BigNumber.Value): TokenAmount {
    if (amount instanceof TokenAmount) {
      return new this.Class(this.value.minus(amount.value), this.decimalPlaces, this.isStablecoin);
    }

    return new this.Class(this.value.minus(amount), this.decimalPlaces, this.isStablecoin);
  }

  saturatingSub(amount: TokenAmount | BigNumber.Value): TokenAmount {
    return this.sub(amount).max(this.Class.ZERO);
  }

  div(amount: BigNumber.Value): TokenAmount {
    return new this.Class(this.value.div(amount), this.decimalPlaces, this.isStablecoin);
  }

  difference(amount: TokenAmount): BigNumber {
    return new BigNumber(this.value).minus(amount.value).div(amount.value).multipliedBy(100);
  }

  mul(amount: BigNumber.Value): TokenAmount {
    return new this.Class(this.value.times(amount), this.decimalPlaces, this.isStablecoin);
  }

  eq(amount: TokenAmount | BigNumber.Value | null | undefined): boolean {
    if (amount === null || amount === undefined) return false;
    if (amount instanceof TokenAmount) {
      return this.decimalPlaces === amount.decimalPlaces && this.value.eq(amount.value);
    }
    return this.value.eq(amount);
  }

  cmp(amount: TokenAmount | BigNumber.Value): -1 | 0 | 1 {
    if (amount instanceof TokenAmount) return this.value.comparedTo(amount.value) ?? 0;
    return this.value.comparedTo(amount) ?? 0;
  }

  lt(amount: TokenAmount | BigNumber.Value): boolean {
    if (amount instanceof TokenAmount) return this.value.lt(amount.value);
    return this.value.lt(amount);
  }

  lte(amount: TokenAmount | BigNumber.Value): boolean {
    if (amount instanceof TokenAmount) return this.value.lte(amount.value);
    return this.value.lte(amount);
  }

  gt(amount: TokenAmount | BigNumber.Value): boolean {
    if (amount instanceof TokenAmount) return this.value.gt(amount.value);
    return this.value.gt(amount);
  }

  gte(amount: TokenAmount | BigNumber.Value): boolean {
    return !this.lt(amount);
  }

  abs(): TokenAmount {
    return new this.Class(this.value.abs(), this.decimalPlaces, this.isStablecoin);
  }

  max(other: TokenAmount): TokenAmount {
    return this.gt(other) ? this : other;
  }

  min(other: TokenAmount): TokenAmount {
    return this.lt(other) ? this : other;
  }

  /**
   * @returns a ratio of this amount to the given amount, e.g. if this amount
   *          is 1 and the given amount is 2, the ratio will be 0.5.
   */
  ratio(amount: TokenAmount): BigNumber {
    return new BigNumber(this.toFixed()).div(amount.toFixed());
  }

  toNumber(): number {
    return this.toBigNumber().toNumber();
  }

  toBigNumber(): BigNumber {
    this.bigNumber ??= this.value.shiftedBy(-this.decimalPlaces);
    return this.bigNumber;
  }

  toBigInt(): bigint {
    return BigInt(this.toString());
  }

  toUsdNumber(price: BigNumber.Value | undefined): number {
    const amount = this.toBigNumber();
    if (amount.isNaN()) return 0;
    return (
      amount
        .multipliedBy(price ?? 0)
        // We use 10 decimal places for representing USD values on the backend
        // Specifying 10 decimal places here provides safety when doing arithmetic operations (with those backend values) on the frontend.
        .decimalPlaces(10)
        .toNumber()
    );
  }

  toUsdFormatted(price: BigNumber.Value | undefined): string {
    const amount = this.toBigNumber();
    if (amount.isNaN()) return '$0.00';
    return formatUsdValue(amount.multipliedBy(price ?? 0).toFixed());
  }

  /**
   * @returns the token amount as its "whole" unit with as many decimal places
   *          as required, e.g. for Ether it will return the amount in Ether,
   *          not in Wei.
   */
  toFixed(decimals?: number, roundingMode: RoundingMode = 'ROUND_HALF_UP'): string {
    return decimals === undefined
      ? this.toBigNumber().toFixed()
      : this.toBigNumber().toFixed(decimals, ROUNDING_MODE[roundingMode]);
  }

  /**
   * @returns
   * - default format: 123,000.12345678
   * - numeral format: 0, 10,000, 1M, 10b etc.
   * - dust format: <0.00000001
   * - noPad option: 123.00 -> 123
   */
  toFormatted(
    format: 'numeral' | undefined = undefined,
    decimals?: number,
    roundingMode: RoundingMode = 'ROUND_DOWN',
    noPad: boolean = false,
  ): string {
    let bigNumber = this.toBigNumber();
    let max = 8;
    let sign = '';

    if (bigNumber.isNegative()) {
      sign = '-';
      bigNumber = bigNumber.abs();
    }

    if (bigNumber.isZero() && format === 'numeral') return '0';
    if (bigNumber.isZero()) return '0.00';

    if (bigNumber.lt(1e-2) && format === 'numeral') return `<${sign}0.01`;
    if (bigNumber.lt(1e-8) && !decimals) return `<${sign}0.00000001`;

    if (format === 'numeral') {
      return `${sign}${formatWithNumeral(bigNumber, false)}`;
    }

    // stablecoins don't need more than cent precision above 1 unit. the decimals check stays for
    // amounts built without asset context, and for Trx, which has 6 decimals but is not a stable
    if ((this.isStablecoin || this.decimalPlaces === 6) && this.toBigNumber().gt(1)) {
      max = 2;
    }

    const rounded = bigNumber.decimalPlaces(
      decimals ?? Math.min(this.decimalPlaces, max),
      ROUNDING_MODE[roundingMode],
    );

    if (rounded.isZero()) sign = '';

    const formatted = rounded.toFormat(decimals ?? Math.min(this.decimalPlaces, max));

    return `${sign}${removeTrailingZeros(formatted, noPad)}`;
  }

  /**
   * @returns
   *  - BTC: 1,234.12345
   *  - Everything else: 1,234.123456
   */
  toCapped(roundingMode: RoundingMode = 'ROUND_DOWN', noPad = false): string {
    let decimals = 5;
    // BTC
    if (this.decimalPlaces === 8) {
      decimals = 6;
    }
    return this.toFormatted(undefined, decimals, roundingMode, noPad);
  }

  /**
   * @returns the token amount as its base unit, e.g. for Ether it will return
   *          the amount in wei. You probably don't want this one for display
   *          purposes.
   */
  toString(): string {
    return this.value.toFixed();
  }

  toJSON() {
    return {
      [STRINGIFIED_TOKEN_AMOUNT_FLAG]: true,
      value: this.value.toString(),
      decimalPlaces: this.decimalPlaces,
      isStablecoin: this.isStablecoin,
    };
  }

  isNaN(): boolean {
    return this.value.isNaN();
  }

  negated(): TokenAmount {
    return new this.Class(this.value.negated(), this.decimalPlaces, this.isStablecoin);
  }
}

export const stringifiedTokenAmountReviver = (_key: string, value: unknown) => {
  const isObject = value && typeof value === 'object';
  if (isObject && STRINGIFIED_TOKEN_AMOUNT_FLAG in value) {
    const {
      value: tokenAmountValue,
      decimalPlaces,
      isStablecoin,
    } = value as unknown as ReturnType<TokenAmount['toJSON']>;

    return new TokenAmount(tokenAmountValue, decimalPlaces, isStablecoin);
  }
  return value;
};
